import { useState } from "react";
import { Check, Package, Search, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

const STEPS = ["pending", "confirmed", "processing", "shipped", "delivered"];

export default function TrackOrder() {
    const { t } = useTranslation();
    const [orderId, setOrderId] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [order, setOrder] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setOrder(null);
        try {
            const { data } = await api.get("/orders/track", {
                params: { order_id: orderId.trim(), email: email.trim() },
            });
            setOrder(data);
        } catch {
            setError(t("trackOrder.notFound"));
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setOrder(null);
        setOrderId("");
        setEmail("");
        setError("");
    };

    const stepIndex = order ? STEPS.indexOf(order.status) : -1;
    const isCancelled = order?.status === "cancelled";

    return (
        <div className="max-w-[700px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24" data-testid="track-order-page">
            <div className="mb-10 sm:mb-12">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">
                    {t("trackOrder.eyebrow")}
                </div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                    {t("trackOrder.title")}
                </h1>
                <p className="text-neutral-600 mt-3 text-sm sm:text-base">{t("trackOrder.subtitle")}</p>
            </div>

            {!order && (
                <form onSubmit={submit} className="border border-neutral-200 rounded-xl p-5 sm:p-8 space-y-5" data-testid="track-order-form">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2 block">
                            {t("trackOrder.refLabel")}
                        </label>
                        <input
                            required
                            value={orderId}
                            onChange={(e) => setOrderId(e.target.value)}
                            placeholder={t("trackOrder.refPlaceholder")}
                            className="w-full font-mono border border-neutral-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#0089c8]"
                            data-testid="track-order-ref-input"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2 block">
                            {t("trackOrder.emailLabel")}
                        </label>
                        <input
                            required
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("trackOrder.emailPlaceholder")}
                            className="w-full border border-neutral-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#0089c8]"
                            data-testid="track-order-email-input"
                        />
                    </div>
                    {error && (
                        <div className="text-xs text-red-600" data-testid="track-order-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-60 rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs py-4 transition-colors"
                        data-testid="track-order-submit-btn"
                    >
                        <Search className="w-4 h-4" />
                        {loading ? t("trackOrder.submitting") : t("trackOrder.submit")}
                    </button>
                </form>
            )}

            {order && (
                <div className="border border-neutral-200 rounded-xl p-5 sm:p-8 space-y-8" data-testid="track-order-result">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">
                                {t("order.reference")}
                            </div>
                            <div className="font-mono text-[#0077b6] font-bold" data-testid="track-order-id">
                                {order.id}
                            </div>
                            <div className="text-xs text-neutral-500 mt-1">
                                {t("trackOrder.placedOn", { date: new Date(order.created_at).toLocaleDateString() })}
                            </div>
                        </div>
                        <div className="font-display text-2xl font-extrabold text-[#0077b6]" data-testid="track-order-total">
                            £{Number(order.total).toFixed(2)}
                        </div>
                    </div>

                    {isCancelled ? (
                        <div className="flex items-center gap-3 border border-red-200 bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm" data-testid="track-order-cancelled">
                            <XCircle className="w-5 h-5 flex-shrink-0" />
                            {t("trackOrder.cancelled")}
                        </div>
                    ) : (
                        <div data-testid="track-order-steps">
                            <div className="flex items-center">
                                {STEPS.map((step, i) => (
                                    <div key={step} className="flex-1 flex items-center last:flex-none">
                                        <div className="flex flex-col items-center gap-2 flex-shrink-0">
                                            <div
                                                className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${
                                                    i <= stepIndex
                                                        ? "bg-[#0089c8] border-[#0089c8] text-white"
                                                        : "bg-white border-neutral-200 text-neutral-400"
                                                }`}
                                                data-testid={`track-step-${step}`}
                                            >
                                                {i < stepIndex ? <Check className="w-3.5 h-3.5" /> : <span className="text-[10px] font-bold">{i + 1}</span>}
                                            </div>
                                            <div className={`text-[9px] sm:text-[10px] uppercase tracking-[0.14em] text-center ${i <= stepIndex ? "text-[#0077b6] font-bold" : "text-neutral-400"}`}>
                                                {t(`trackOrder.step${step.charAt(0).toUpperCase()}${step.slice(1)}`)}
                                            </div>
                                        </div>
                                        {i < STEPS.length - 1 && (
                                            <div className={`h-0.5 flex-1 mx-1 sm:mx-2 mb-4 ${i < stepIndex ? "bg-[#0089c8]" : "bg-neutral-200"}`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border-t border-neutral-200 pt-5">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-3">
                            {t("trackOrder.itemsTitle")}
                        </div>
                        <div className="space-y-2">
                            {order.items.map((it, idx) => (
                                <div key={`${it.name}-${it.dose || "nodose"}-${idx}`} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <Package className="w-3.5 h-3.5 text-neutral-400" />
                                        {it.name} {it.dose && <span className="font-mono text-[10px] text-[#0077b6]">{it.dose}</span>}
                                    </div>
                                    <div className="text-neutral-500 font-mono">×{it.quantity}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={reset}
                        className="text-xs uppercase tracking-[0.18em] text-neutral-600 hover:text-neutral-900"
                        data-testid="track-order-another-btn"
                    >
                        {t("trackOrder.another")}
                    </button>
                </div>
            )}
        </div>
    );
}
